import { gift, Icon } from '../../components/Icon/Icon';
import { Routing } from '../../components/Routes';
import { useConfig } from '../../config';
import { useFormatting, useTranslation } from '../../i18n/i18n';
import { Link, Redirect, useHistory } from 'react-router-dom';
import { Res, useApi } from '../../state/api';
import { ApiBonusActivate, ApiOrder, ApiThanks } from '../../types';
import { Main } from '../Main/Main';
import './CheckoutThanksPage.css';
import { useEffect, useRef } from 'react';
import { ConversionItem, orderItemToEcommerceItem, useAnalytics } from '../../hooks/useAnalytics';
import { useCart } from '../../state/cart';
import { useQuery } from '../../hooks/useQuery';
import { renderSnippet } from '../../components/checkout/KlarnaCheckout';
import { Button } from '../../components/Form/Button';
import axios from 'axios';
import { useCriteo } from '../../hooks/useCriteo';
import { useLoop54Events } from '../../hooks/useLoop54Events';
import { MetaContainer, SearchEngine } from '../../state/meta';
import { cookieService } from '../cookieService';

const TORK_FACEBOOK_PIXEL_ID = '1130257161678352';

declare global {
	interface Window {
		fbq: any;
		uetq: any;
		criteo_q: any;
		AWIN: any;
		plausible: any;
	}
}

function CheckoutThanksPage() {
	const query = useQuery();
	const sid = query.get('sid');
	const { data, isLoading } = useApi(Res.thanks, sid ? { sid } : {});
	const { mutate: mutateSession } = useApi(Res.session, {});
	const config = useConfig();
	const history = useHistory();
	const t = useTranslation();
	const { generic, thanksPage } = t;
	const urls = t.routes;
	const { sendEvent, setUser, sendTorkEvent, sendGoogleAdsPurchaseConversion } = useAnalytics();
	const { invalidate: invalidateCart } = useCart();
	const klarnaRef = useRef<HTMLDivElement>(null);
	const initGtagEventCalledRef = useRef(false);
	const { asCurrency } = useFormatting();
	const awinDivRef = useRef<HTMLDivElement>(null);
	const { sendCriteoEvent } = useCriteo();
	const { store } = config;
	const { sendLoop54Event } = useLoop54Events();
	const { searchProfileId } = MetaContainer.useContainer();
	const { marketing } = MetaContainer.useContainer().trackingConsents;
	const actions = data?.actions ?? [];
	const bonus = data?.bonus;

	useEffect(() => {
		if (klarnaRef?.current && data?.klarnaSnippet && data.klarnaSnippet !== '') {
			renderSnippet(klarnaRef.current, data.klarnaSnippet);
		}

		if (data !== undefined && config.criteoId) {
			sendCriteoEvent({
				event: 'trackTransaction',
				id: data.order.id + '',
				item: data.order.items.map((item) => ({
					id: item.product.id,
					quantity: item.quantity,
					price: item.product.finalPrice,
				})),
			});
		}

		// Awin tracking
		if (data?.order && config.store === 'kgse') {
			const parser = new DOMParser();
			const awinMarkup = parser.parseFromString(awinTrackingMarkup(data.order), 'text/html');
			awinDivRef.current?.appendChild(awinMarkup.body.firstChild!);
			//@ts-ignore
			if (typeof window !== 'undefined') {
				//@ts-ignore
				if (typeof window.AWIN !== 'undefined' && typeof window.AWIN.Tracking !== 'undefined') {
					window.AWIN.Tracking.Sale = {};
					window.AWIN.Tracking.Sale.amount = data.order.totals.total;
					window.AWIN.Tracking.Sale.channel = 'aw';
					window.AWIN.Tracking.Sale.orderRef = data.order.id;
					window.AWIN.Tracking.Sale.parts = `KATEGORI0:${data.order.totals.total}`;
					window.AWIN.Tracking.Sale.currency = config.currencyCode;
					window.AWIN.Tracking.Sale.voucher = '';
					window.AWIN.Tracking.run();
				}
			}

			return () => {
				if (awinDivRef.current) {
					const child = awinDivRef.current.firstChild;
					if (child) {
						awinDivRef.current.removeChild(child);
					}
				}
			};
		}
	}, [data]);

	useEffect(() => {
		invalidateCart();
		mutateSession();
	}, []);

	function sendPurchaseEvent() {
		if (!data || initGtagEventCalledRef.current) return;
		initGtagEventCalledRef.current = true;

		if (actions.includes('TRACK_GA_CONVERSION')) {
			if (data.h?.e) {
				setUser(data.h.e);
			}
			sendEvent({
				event: 'purchase',
				ecommerce: {
					send_to: config.ga4Id,
					transaction_id: String(data.order.id),
					value: data.order.totals.total - data.order.totals.vat,
					currency: config.localCurrency,
					tax: data.order.totals.vat,
					shipping: data.order.totals.shippingFee,
					items: data.order.items.map(orderItemToEcommerceItem),
				},
			});

			if (config.googleAdsConversion) {
				const value = data.order.totals.total - data.order.totals.vat;
				sendGoogleAdsPurchaseConversion({
					transactionId: String(data.order.id),
					value: value > 15000 ? 15000 : value,
					currency: config.localCurrency,
					newCustomer: data.newCustomer ?? false,
					sendTo: config.googleAdsConversion,
					merchantId: config.googleMerchantId,
					feedCountry: config.country,
					feedLanguage: config.lang,
					discount: data.order.totals.discount,
					items: data.order.items.map(
						(item): ConversionItem => ({
							id: String(item.product.id),
							quantity: item.quantity,
							price: item.product.finalPrice,
						})
					),
				});

				if (typeof window.fbq !== 'undefined') {
					if (config.facebookPixelId !== '') {
						window.fbq('trackSingle', config.facebookPixelId, 'Purchase', {
							content_type: 'product',
							content_ids: [data.order.items.map((item) => item.product.id)],
							contents: data.order.items.map((item) => ({
								id: item.product.id,
								quantity: item.quantity,
							})),
							value: data.order.totals.total - data.order.totals.vat,
							currency: config.localCurrency,
						});
					}
				}

				if (typeof window.uetq !== 'undefined') {
					if (config.store === 'kgno') {
						window.uetq.push('event', 'purchase', {
							revenue_value: data.order.totals.total - data.order.totals.vat,
							currency: config.currencyCode,
						});
					} else {
						window.uetq.push({
							ec: 'Purchase',
							ea: 'Completed',
							el: '',
							ev: 1,
							gv: '' + (data.order.totals.total - data.order.totals.vat),
							gc: config.currencyCode,
						});
					}
				}
			}

			const cookieSearchEngine = cookieService.read('searchEngine') as SearchEngine;
			let se = !cookieSearchEngine ? 'none' : cookieSearchEngine;
			if (cookieSearchEngine === 'not-set') se = 'none';
			if (typeof window.plausible !== 'undefined') {
				window.plausible('Purchase', {
					revenue: {
						currency: config.currencyCode,
						amount: data.order.totals.total - data.order.totals.vat,
					},
					props: { purchaseSearchEngine: se },
				});
			}
		}

		if (actions.includes('GOOGLE_REVIEW') && data.estDelivery) {
			if (typeof window === 'undefined') return;
			const estimatedDeliveryDate = data.estDelivery.split('T')[0];
			// @ts-ignore
			const gapi = window.gapi;
			if (!gapi?.load) return;
			gapi.load('surveyoptin', () => {
				gapi.surveyoptin.render({
					merchant_id: config.googleSurveyId,
					order_id: String(data.order.id),
					email: data.order.customer.email,
					estimated_delivery_date: estimatedDeliveryDate,
					delivery_country: config.country,
				});
			});
		}
	}

	function awinTrackingMarkup(order: ApiOrder) {
		const formOrderItems = order.items.map(
			(orderItem) =>
				`AW:P|8936|${orderItem.id}|${orderItem.product.id}|${orderItem.product.name}|${asCurrency(
					orderItem.product.finalPrice
				)}|${orderItem.quantity}|st|KATEGORI0|`
		);
		const markup = `<div>
	<img border="0" height="0" src="https://www.awin1.com/sread.img?tt=ns&tv=2&merchant=8936&amount=${
		order.totals.total
	}&ch=aw&cr=SEK&parts=KATEGORI0:${order.totals.total}&ref=${order.id}&vc=&p1=&p2=" style="display: none;" width="0">

	<form style="display: none;" name="aw_basket_form">
	<textarea wrap="physical" id="aw_basket">
	${formOrderItems.join('\n')}
	</textarea>
	</form>
</div>`;

		return markup;
	}

	async function handleActivateBonus() {
		if (!data?.bonus?.code) return;

		try {
			await axios.post<ApiBonusActivate>(
				config.api + Res.bonusActivate.uri({}),
				{
					c: data!.bonus.code,
					orderId: data!.order.id,
				},
				{ withCredentials: true }
			);
		} catch (e) {
			// the page that follows congratulates them on joining, so it must not be reached
			// unless they have - and the button does not await this either
			return;
		}

		mutateSession();
		history.push(`${urls.bonus}?activated=true`);
	}

	useEffect(() => {
		if (data) {
			sendPurchaseEvent();
		}
	}, [data]);

	useEffect(() => {
		if (data && searchProfileId) {
			sendLoop54Event({
				events: data.order.items.map((item) => ({
					type: 'purchase',
					entity: { type: 'Product', id: item.product.id + '' },
					revenue: item.product.finalPrice,
					orderId: data.order.id,
					quantity: item.quantity,
				})),
			});
		}
	}, [searchProfileId, data]);

	// tork Purchase event tracking for kgno with persisted last tracked order id
	useEffect(() => {
		if (store !== 'kgno') return;

		const orderId = data?.order?.id ? String(data.order.id) : null;
		if (!orderId || !data) return;

		const lastTracked = sessionStorage.getItem('lastTrackedTorkOrderId');

		if (lastTracked === orderId) return;

		const order = data.order;
		const torkItems = order.items.filter((item) => item.product.name.toLowerCase().includes('tork'));
		if (torkItems.length === 0) return;

		const torkTotal = torkItems.reduce((sum, item) => sum + item.product.finalPrice * item.quantity, 0);
		if (torkTotal <= 0) return;

		// slight delay to allow any async rendering to settle
		setTimeout(() => {
			sendTorkEvent('Purchase', {
				revenue: torkTotal,
				transactionId: String(data.order.id),
			});

			sessionStorage.setItem('lastTrackedTorkOrderId', orderId);
		}, 0);
	}, [data?.order?.id]);

	// Tork Meta Purchase event tracking for kgno
	useEffect(() => {
		if (store !== 'kgno' || !marketing) return;

		const orderId = data?.order?.id ? String(data.order.id) : null;
		if (!orderId || !data || typeof window.fbq === 'undefined') return;

		if (sessionStorage.getItem('lastTrackedTorkMetaOrderId') === orderId) return;

		const torkItems = data.order.items.filter((item) => item.product.name.toLowerCase().includes('tork '));
		const torkTotal = torkItems.reduce((sum, item) => sum + item.product.finalPrice * item.quantity, 0);
		if (torkItems.length === 0 || torkTotal <= 0) return;

		setTimeout(() => {
			window.fbq('trackSingle', TORK_FACEBOOK_PIXEL_ID, 'Purchase', {
				content_type: 'product',
				content_ids: torkItems.map((item) => item.product.id),
				contents: torkItems.map((item) => ({
					id: item.product.id,
					quantity: item.quantity,
				})),
				value: torkTotal,
				currency: 'NOK',
			});

			sessionStorage.setItem('lastTrackedTorkMetaOrderId', orderId);
		}, 0);
	}, [data?.order?.id, marketing]);

	if (!data && !isLoading) {
		return <Redirect to="/" />;
	}

	if (!data || isLoading) {
		return <Main>{generic.loading}</Main>;
	}

	const shouldShowRegularThanks = !data.klarnaSnippet || data.klarnaSnippet === '';

	return (
		<Main>
			<div className="thanks-container">
				<div className={!shouldShowRegularThanks ? 'klarna-container' : ''} ref={klarnaRef} />

				{shouldShowRegularThanks && (
					<ThanksCard customerServicephone={config.customerServicePhoneNumber} data={data} />
				)}

				{bonus && (
					<div className="bonus-container">
						{bonus.amount > 0 && <BonusCard bonus={bonus} />}

						{!isLoading && !bonus.active && bonus.code && (
							<Button onClick={handleActivateBonus} className="bonus-button">
								{thanksPage.activateBonus}
							</Button>
						)}

						{!isLoading && bonus.active && (
							<Link to={urls.bonus} className="bonus-button">
								{thanksPage.showBonus}
							</Link>
						)}
					</div>
				)}
			</div>
			{config.store === 'kgse' && <div ref={awinDivRef} />}
		</Main>
	);
}

function ThanksCard(props: { data: ApiThanks; customerServicephone: string }) {
	const thanksPage = useTranslation().thanksPage;

	return (
		<div className="thanks-card">
			<h1>{thanksPage.thanksForOrdering}</h1>
			<p>{thanksPage.thanksInformation}</p>
			<p>
				{thanksPage.confirmationEmail} <strong>{props.data.order.customer.email}</strong>
			</p>
			<p>
				{thanksPage.orderNumber} <strong>{props.data.order.id}</strong>
			</p>
			<p>
				{thanksPage.ifQuestions} <strong>{props.customerServicephone}</strong>
			</p>
			<p>{thanksPage.thanksAgain}</p>
		</div>
	);
}

function BonusCard(props: { bonus: NonNullable<ApiThanks['bonus']> }) {
	const thanksPage = useTranslation().thanksPage;
	const f = useFormatting();
	const { currencyCode } = useConfig();

	return (
		<div className="bonus-card">
			<Icon shape={gift} size={120} />
			<h2>
				{' '}
				{f.asPrice(props.bonus.amount, currencyCode === 'EUR' ? 2 : 0)} {thanksPage.bonusBack}
			</h2>
			<p>
				{thanksPage.youGot} <strong>{f.asPrice(props.bonus.amount, currencyCode === 'EUR' ? 2 : 0)}</strong>{' '}
				{thanksPage.inYourAccount}
			</p>
		</div>
	);
}

export const checkoutThanksRouting: Routing = {
	path(urls) {
		return urls.checkoutThanks;
	},
	component: CheckoutThanksPage,
	requiresAuth: false,
};

export const companyCheckoutThanksRouting: Routing = {
	path(urls) {
		return urls.companyCheckoutThanks;
	},
	component: CheckoutThanksPage,
	requiresAuth: false,
};
